import * as ex from 'excalibur';
import { EmotionType } from '../types/enums';
import { Reaction } from '../types/interfaces';
import { Animal } from './animal';

/**
 * Manages emotions and reactions for animals in the ecosystem
 */
export class EmotionSystem {
  private static instance: EmotionSystem;
  private activeReactions: Map<Animal, Reaction> = new Map();
  private reactionLabels: Map<Animal, ex.Label> = new Map();
  private reactionBackgrounds: Map<Animal, ex.Actor> = new Map();
  private readonly MAX_LOG_SIZE: number = 20;

  // Singleton pattern
  public static getInstance(): EmotionSystem {
    if (!EmotionSystem.instance) {
      EmotionSystem.instance = new EmotionSystem();
    }
    return EmotionSystem.instance;
  }

  private constructor() {
    // Private constructor for singleton
  }

  // Track recent reactions to implement cooldowns
  private recentReactions: Map<Animal, Map<EmotionType, number>> = new Map();
  private readonly REACTION_COOLDOWN: number = 10000; // 10 seconds cooldown for the same emotion type

  /**
   * Add a new reaction to an animal
   */
  public addReaction(animal: Animal, emotionType: EmotionType, duration: number = 2000, intensity: number = 1.0, cause?: Animal, message?: string): void {
    const currentTime = Date.now();
    
    // Check cooldown for this emotion type on this animal
    if (!this.canAddReaction(animal, emotionType, currentTime)) {
      return; // Still on cooldown, don't add the reaction
    }
    
    // Create the reaction
    const reaction: Reaction = {
      emotionType,
      timestamp: currentTime,
      duration,
      intensity,
      cause,
      message
    };

    // Add to the animal's reaction log (maintaining max size)
    animal.reactionLog.unshift(reaction);
    if (animal.reactionLog.length > this.MAX_LOG_SIZE) {
      animal.reactionLog.pop();
    }
    
    // Record this reaction time for cooldown tracking
    this.trackReactionTime(animal, emotionType, currentTime);

    // Set as the current reaction if appropriate
    if (this.shouldOverrideCurrentReaction(animal, reaction)) {
      animal.currentReaction = reaction;
      this.activeReactions.set(animal, reaction);
      this.displayReaction(animal, reaction);
    }
  }
  
  /**
   * Check if a reaction can be added based on cooldown times
   */
  private canAddReaction(animal: Animal, emotionType: EmotionType, currentTime: number): boolean {
    // Get the map of recent reactions for this animal
    const animalReactions = this.recentReactions.get(animal);
    if (!animalReactions) {
      return true; // No recent reactions for this animal
    }
    
    // Get the last time this emotion type was triggered
    const lastTriggered = animalReactions.get(emotionType);
    if (!lastTriggered) {
      return true; // This emotion type hasn't been triggered recently
    }
    
    // Check if enough time has passed (cooldown expired)
    return (currentTime - lastTriggered) >= this.REACTION_COOLDOWN;
  }
  
  /**
   * Track when a reaction type was last triggered for cooldown
   */
  private trackReactionTime(animal: Animal, emotionType: EmotionType, timestamp: number): void {
    // Get or create the map for this animal
    let animalReactions = this.recentReactions.get(animal);
    if (!animalReactions) {
      animalReactions = new Map<EmotionType, number>();
      this.recentReactions.set(animal, animalReactions);
    }
    
    // Record the timestamp for this emotion type
    animalReactions.set(emotionType, timestamp);
  }

  /**
   * Update all active reactions (called once per frame)
   */
  public update(delta: number): void {
    const currentTime = Date.now();
    const animalsToUpdate = Array.from(this.activeReactions.keys());

    for (const animal of animalsToUpdate) {
      const reaction = this.activeReactions.get(animal);
      if (!reaction) continue;

      // Check if the reaction has expired
      if (currentTime - reaction.timestamp >= reaction.duration) {
        this.removeReaction(animal);
        this.findNextReaction(animal);
      }
    }
  }

  /**
   * Determine if a new reaction should override the current one
   */
  private shouldOverrideCurrentReaction(animal: Animal, newReaction: Reaction): boolean {
    const currentReaction = animal.currentReaction;

    // If no current reaction, always accept the new one
    if (!currentReaction) {
      return true;
    }

    // Check if current reaction has expired
    if (Date.now() - currentReaction.timestamp >= currentReaction.duration) {
      return true;
    }

    // Priority rules:
    // 1. More intense reactions override less intense ones
    if (newReaction.intensity && currentReaction.intensity &&
      newReaction.intensity > currentReaction.intensity) {
      return true;
    }

    // 2. Certain emotion types have priority
    const priorityOrder = this.getEmotionPriority(newReaction.emotionType);
    const currentPriority = this.getEmotionPriority(currentReaction.emotionType);

    return priorityOrder < currentPriority;
  }

  /**
   * Get priority value for an emotion type (lower = higher priority)
   */
  private getEmotionPriority(emotionType: EmotionType): number {
    const priorities: { [key in EmotionType]: number } = {
      [EmotionType.Frightened]: 1,   // Highest priority - fear
      [EmotionType.Biting]: 2,       // Active attack
      [EmotionType.Angry]: 3,
      [EmotionType.Stalking]: 4,
      [EmotionType.Alert]: 5,
      [EmotionType.Hungry]: 6,
      [EmotionType.Thirsty]: 7,
      [EmotionType.Tired]: 8,
      [EmotionType.Foraging]: 9,
      [EmotionType.Curious]: 10,
      [EmotionType.Sleeping]: 11     // Lowest priority
    };

    return priorities[emotionType] || 999;
  }

  /**
   * Display a reaction above the animal
   */
  private displayReaction(animal: Animal, reaction: Reaction): void {
    // Remove any existing reaction display
    this.removeReactionDisplay(animal);

    // Create a new label for the reaction text
    const labelText = reaction.emotionType.toUpperCase();
    const label = new ex.Label({
      text: labelText,
      pos: new ex.Vector(-1, 1), // Position above the animal
      font: new ex.Font({
        family: 'VT323, monospace',
        size: 8,
        unit: ex.FontUnit.Px,
        textAlign: ex.TextAlign.Center
      }),
      color: this.getColorForEmotion(reaction.emotionType),
    });

    // Create a background for the label
    const padding = 1.5;
    const background = new ex.Actor({
      pos: new ex.Vector(0, -20),
      width: label.getTextWidth() / 2.0 + padding * 2,
      height: 12,
      color: ex.Color.fromHex('#000000BB'), // Semi-transparent black
    });

    background.graphics.use(new ex.Rectangle({
      width: label.getTextWidth() / 2.0 + padding * 2,
      height: 10,
      color: ex.Color.fromHex('#000000BB'),
    }));

    // Add the label and background to the animal
    animal.addChild(background);
    background.addChild(label);

    // Add a function to check the animal's scale and reverse it for the text
    animal.on('postupdate', () => {
      if (animal.scale.x < 0) {
        // If animal is flipped (facing right), flip the text back
        background.scale.x = -1;
      } else {
        // If animal is normal (facing left), keep text normal
        background.scale.x = 1;
      }
    });

    // Store references for cleanup
    this.reactionLabels.set(animal, label);
    this.reactionBackgrounds.set(animal, background);
  }

  /**
   * Remove the visual display of a reaction
   */
  private removeReactionDisplay(animal: Animal): void {
    // Remove the label if it exists
    const label = this.reactionLabels.get(animal);
    const background = this.reactionBackgrounds.get(animal);

    if (background) {
      animal.removeChild(background);
      this.reactionBackgrounds.delete(animal);
    }

    if (label) {
      if (background) {
        background.removeChild(label);
      } else {
        animal.removeChild(label);
      }
      this.reactionLabels.delete(animal);
    }
  }

  /**
   * Remove a reaction from an animal
   */
  private removeReaction(animal: Animal): void {
    // Remove from active reactions
    this.activeReactions.delete(animal);

    // Clear the visual display
    this.removeReactionDisplay(animal);

    // Clear the current reaction
    animal.currentReaction = null;
    
    // Clean up old entries in the recentReactions map to prevent memory leaks
    // We do this when removing reactions to avoid building up stale references
    const currentTime = Date.now();
    
    // Clean up any expired cooldowns
    this.cleanupExpiredCooldowns(currentTime);
  }
  
  /**
   * Clean up expired cooldown entries to prevent memory leaks
   */
  private cleanupExpiredCooldowns(currentTime: number): void {
    // For each animal in the recentReactions map
    for (const [animal, reactions] of this.recentReactions.entries()) {
      let shouldRemoveAnimal = true;
      
      // For each emotion type, check if its cooldown has expired
      for (const [emotionType, timestamp] of reactions.entries()) {
        if (currentTime - timestamp < this.REACTION_COOLDOWN) {
          // At least one cooldown is still active
          shouldRemoveAnimal = false;
        } else {
          // This cooldown has expired, remove it
          reactions.delete(emotionType);
        }
      }
      
      // If all cooldowns for this animal have expired, remove the animal entry
      if (shouldRemoveAnimal || reactions.size === 0) {
        this.recentReactions.delete(animal);
      }
    }
  }

  /**
   * Find the next valid reaction to display after removing the current one
   */
  private findNextReaction(animal: Animal): void {
    // Look through the reaction log for the most recent valid reaction
    const currentTime = Date.now();

    for (const reaction of animal.reactionLog) {
      // Skip expired reactions
      if (currentTime - reaction.timestamp >= reaction.duration) {
        continue;
      }

      // Found a valid reaction to display
      animal.currentReaction = reaction;
      this.activeReactions.set(animal, reaction);
      this.displayReaction(animal, reaction);
      return;
    }
  }

  /**
   * Get a color for an emotion type
   */
  public getColorForEmotion(emotionType: EmotionType): ex.Color {
    const colors: { [key in EmotionType]: ex.Color } = {
      [EmotionType.Hungry]: ex.Color.fromHex('#FFA500'),      // Orange
      [EmotionType.Thirsty]: ex.Color.fromHex('#00BFFF'),     // Light blue
      [EmotionType.Tired]: ex.Color.fromHex('#D3D3D3'),       // Light gray
      [EmotionType.Angry]: ex.Color.fromHex('#FF0000'),       // Red
      [EmotionType.Stalking]: ex.Color.fromHex('#800080'),    // Purple
      [EmotionType.Foraging]: ex.Color.fromHex('#32CD32'),    // Lime green
      [EmotionType.Biting]: ex.Color.fromHex('#8B0000'),      // Dark red
      [EmotionType.Sleeping]: ex.Color.fromHex('#87CEEB'),    // Sky blue
      [EmotionType.Frightened]: ex.Color.fromHex('#FFFF00'),  // Yellow
      [EmotionType.Alert]: ex.Color.fromHex('#FF6347'),       // Tomato
      [EmotionType.Curious]: ex.Color.fromHex('#9370DB')      // Medium purple
    };

    return colors[emotionType] || ex.Color.White;
  }
}
